import {ActivityFlightState, Leg} from "../../types-enums/activity-flight";
import {getFlightFailedOrSuccessfulLegs, getTravelClassString} from "../getter/flight";
import {i18n} from "i18next";
import {
    CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL,
    CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL
} from "../../api/model/common";
import {AirportResponse, FlightsResponse} from "../../api/model/activity-flight";
import {getBackendCalculationStatus} from "../getter/emissions";
import {sumActivitiesEmissionResponseTotalEmissionsKgCo2} from "../getter/co2-getter";
import {
    ADDITIONAL_FLIGHT_INFORMATION_SEPARATOR,
    FLIGHT_CALCULATION_HINT,
    formatCo2EPerPersonString,
    formatCo2ETotalString,
    formatDateString,
    generateFlightTypeString,
    generatePassengerString,
    ROUTE_SEPARATOR
} from "./common";

/**
 * Determines if all valid flight data is used for flight calculations.
 *
 * This function assesses an array of flight legs and iterates through them
 * in steps of two. It checks whether specific properties in a departure
 * leg are null, specifically `travelClass`, `aircraftType`, and `airline`.
 * If any of these properties are null for a given departure leg,
 * the function returns `false`. If no such cases are found, the function
 * returns `true`.
 *
 * @param {Leg[]} legs - An array of `Leg` objects representing flight data.
 * @returns {boolean} Returns `true` if all departure legs have valid `travelClass`,
 * `aircraftType`, and `airline` properties; otherwise, returns `false`.
 */
const onlyGoodDataUsedForFlightCalculationCalculation = (legs: Leg[]) => {
    for (let i = 0; i < legs.length; i += 2) {
        const departure = legs[i];
        if (departure.travelClass === null && departure.aircraftType === null && departure.airline === null) return false
    }
    return true
}

/**
 * Generates a professional flight route string without CO2 equivalent (CO2e) values, based on flight legs and i18n translations.
 * This function processes an array of flight legs to create a formatted string representation of the flight route. Each leg can include details such as the airline, aircraft type, and travel class, which are translated using the provided internationalization `i18n` instance. The function also determines whether a footnote should be shown, based on the validity of the data used in flight calculations.
 *
 * @param {ActivityFlightState['legs']} legs - Array of flight leg objects, where each leg represents a segment of the flight route.
 * @param {Object} i18n - The internationalization (i18n) object for translating flight details, including travel class.
 * @param {Function} i18n.t - Translation function to localize text based on keys provided.
 * @returns {Object} An object containing the following properties:
 * - `flightRouteString` (string): Formatted string representation of the flight route.
 * - `showFootnote` (boolean): Indicates whether a footnote should be displayed, based on the validity of flight data used.
 * - `separator` (string): The separator used between flight legs in the route string.
 * -> {"flightRouteString":"BER  --- DOH  --- JNB","showFootnote":true,"separator":" --- "}
 */
export const generateProfessionalFlightRouteWithoutCo2KgEStrings = (legs: ActivityFlightState['legs'], i18n: i18n) => {
    const {t} = i18n
    const legsString = legs.map((leg, index) => {
        if (index < legs.length - 1) {
            let travelClass: string | null = null
            if (leg?.travelClass) {
                const possibleTravelClass = getTravelClassString(leg.travelClass)
                if (possibleTravelClass) travelClass = t(possibleTravelClass)
            }
            const stopoverAdditionalInformation = [
                leg?.airline?.iata,
                leg?.aircraftType?.iata,
                travelClass
            ].filter(Boolean).join(ADDITIONAL_FLIGHT_INFORMATION_SEPARATOR)
            return `${leg?.airport?.iata} ${stopoverAdditionalInformation.length === 0 ? "" : `(${stopoverAdditionalInformation})`}`
        } else {
            return leg?.airport?.iata
        }
    }).filter(Boolean).join(`${ROUTE_SEPARATOR}`)
    return {
        flightRouteString: legsString,
        showFootnote: !onlyGoodDataUsedForFlightCalculationCalculation(legs),
        separator: ROUTE_SEPARATOR
    }
}

/**
 * Generates a formatted string representation of a customer's flight route for clipboard use,
 * including optional CO2 emissions data.
 *
 * @param {ActivityFlightState} flight - The flight state object containing flight details such as legs and common data.
 * @param {i18n} i18n - The internationalization object for locale-specific formatting.
 * @param {boolean} withCo2E - Determines whether CO2 emissions data should be included in the generated string.
 * @returns {Object} - An object containing the formatted flight route string (`flightRouteString`) and a flag indicating if a footnote should be shown (`showFootNote`).
 *                   -> {"flightRouteString":"Berlin (BER) --- Doha (DOH)* || Doha (DOH) --- Johannesburg - Johannesburg Int'l (JNB)*","showFootNote":true}
 */
export const generateCustomerFlightRouteForClipboardString = (
    flight: ActivityFlightState,
    i18n: i18n,
    withCo2E: boolean,
) => {
    const {legs, common} = flight
    let flightRouteString
    let showFootNote = false
    if (common) {
        const {resolvedLanguage} = i18n
        const {totalEmissions} = common
        flightRouteString = totalEmissions?.map(stop => {
            let departure: Leg
            let arrival: Leg
            const {index, emissionKgCO2e} = stop
            departure = legs[index]
            arrival = legs[index + 1]
            let {
                flightRouteString,
                onlyUsedAirportsForEmissionCalculation
            } = generateCommonFlightRouteString(departure, arrival, i18n)
            if (onlyUsedAirportsForEmissionCalculation) showFootNote = true
            if (withCo2E) flightRouteString = `${flightRouteString} ${formatCo2ETotalString(emissionKgCO2e!!, resolvedLanguage)}`
            return flightRouteString
        })
            .filter(Boolean)
            .join(" || ")
    }

    return {flightRouteString, showFootNote}
}


/**
 * Generates a formatted string summarizing a flight route along with metadata such as departure/arrival airports,
 * airlines, travel class, and date. Returns the flight route string and a flag indicating whether only specific
 * airports were used for emissions calculation.
 *
 * @function
 * @param {Leg} departure - The departure leg of the flight containing details such as date, airline, travel class, and airport.
 * @param {Leg} arrival - The arrival leg of the flight containing details about the arrival airport.
 * @param {i18n} i18n - The internationalization object containing translation functions and resolved language details.
 * @returns {Object} An object containing:
 *                    - `flightRouteString` (string): A formatted string summarizing the flight route and metadata.
 *                    - `onlyUsedAirportsForEmissionCalculation` (boolean): A flag indicating whether only specific airports
 *                      were used as part of the emission calculation logic.
 *                    -> {"flightRouteString":"Doha (DOH) --- Johannesburg - Johannesburg Int'l (JNB)*","onlyUsedAirportsForEmissionCalculation":true}
 */
const generateCommonFlightRouteString = (departure: Leg, arrival: Leg, i18n: i18n) => {
    let arrivalAirport
    let dateString = ""
    let airlineString = ""
    let travelClassString = ""
    let metaDataString = ""
    const {
        date: departureDateMilliseconds,
        airline: departureAirline,
        travelClass: departureTravelClass,
        airport: departureAirport
    } = departure
    const onlyUsedAirportsForEmissionCalculation = !onlyGoodDataUsedForFlightCalculationCalculation([departure, arrival])
    const {t, resolvedLanguage} = i18n
    if (departureDateMilliseconds) dateString = `${formatDateString(departureDateMilliseconds, resolvedLanguage, false)}: `
    if (departureTravelClass) {
        const travelClass = getTravelClassString(departureTravelClass)
        if (travelClass) travelClassString = t(travelClass)
    }
    if (departureAirline) airlineString = departureAirline.name
    if (arrival) arrivalAirport = arrival.airport
    if (airlineString.length > 0 && travelClassString.length > 0) {
        metaDataString = `, ${airlineString}, ${travelClassString}`
    } else if (airlineString.length > 0) {
        metaDataString = `, ${airlineString}`
    } else if (travelClassString.length > 0) {
        metaDataString = `, ${travelClassString}`
    } else {
        metaDataString = ""
    }

    let routeString = ''
    if (departureAirport !== null && arrivalAirport !== null && typeof arrivalAirport !== 'undefined') routeString = generateDepartureArrivalAirportWithIataString(departureAirport, arrivalAirport)
    if (onlyUsedAirportsForEmissionCalculation) routeString = `${routeString}${FLIGHT_CALCULATION_HINT}`
    return {flightRouteString: `${dateString}${routeString}${metaDataString}`, onlyUsedAirportsForEmissionCalculation}
}

/**
 * Generates a customer flight route string with associated metadata based on
 * the provided flight details and internationalization instance.
 *
 * This function processes flight legs and calculates the flight route strings
 * while also considering additional metadata such as emissions calculation,
 * backend status, and potential error messages.
 *
 * @param {ActivityFlightState} flight - The flight state object containing details about flight legs, emissions, and other flight-related information.
 * @param {i18n} i18n - The internationalization object used for translating error messages and other text.
 * @returns {Object} An object containing:
 *                   - `flightRoute` (string[] | undefined): An array of strings describing the flight route, or undefined if no routes are generated.
 *                   - `showFootnote` (boolean): A flag indicating whether a footnote should be shown based on the emission calculation for the airports.
 */
export const generateCustomerFlightRouteWithMetaDataString = (flight: ActivityFlightState, i18n: i18n) => {
    const {legs} = flight
    const {t} = i18n
    let showFootnote = false
    let flightRoute: string[] | undefined = []
    const {common} = flight
    flightRoute = common?.totalEmissions?.map((_, index) => {
        const departure = legs[index]
        const arrival = legs[index + 1]
        let {
            flightRouteString,
            onlyUsedAirportsForEmissionCalculation
        } = generateCommonFlightRouteString(departure, arrival, i18n)
        if (onlyUsedAirportsForEmissionCalculation) showFootnote = true
        return flightRouteString
    })
    const status = getBackendCalculationStatus(common.totalEmissions)
    if (status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors) flightRoute?.unshift(t("ERRORS.CALCULATION_PARTIAL_FAILURE"))
    return {flightRoute, showFootnote}
}

/**
 * Generates a descriptive string indicating the status (successful or failed) of flight leg calculations,
 * along with emissions information per person and total CO2 emissions.
 *
 * @param {ActivityFlightState} flight - The flight activity state containing flight details such as legs, passenger count, and identifiers.
 * @param {CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL} responseStatus - The response status indicating whether the calculation partially succeeded or failed entirely.
 * @param {i18n} i18n - The internationalization object, containing translation functions and the current resolved language.
 * @returns {string} - A formatted string providing a summary of the flight calculation status, failed or successful legs, and emissions information per person and total.
 *                   -> For the following legs, no emission calculation could be performed: Doha (DOH) - Johannesburg - Johannesburg Int'l (JNB). The emissions are: 1,692 kg CO₂e per person (1,692 kg CO₂e Total)
 */
export const generateFailedOrSuccessfulFlightLegsString = (
    flight: ActivityFlightState,
    responseStatus: CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL,
    i18n: i18n,
) => {
    const {resolvedLanguage, t} = i18n
    let emissionsCo2EString = ""
    const failedLegs = getFlightFailedOrSuccessfulLegs(flight, responseStatus)
    const {passengerCount, returnId} = flight
    const co2ETotal = sumActivitiesEmissionResponseTotalEmissionsKgCo2(flight, true)
    emissionsCo2EString = `${formatCo2EPerPersonString(co2ETotal ?? 0, passengerCount, resolvedLanguage)} ${t("GENERAL.PER_PERSON")} (${formatCo2ETotalString(co2ETotal!, resolvedLanguage)} ${i18n.t("GENERAL.TOTAL")})`
    const failedLegsString = failedLegs?.map(leg => {
        const departureLeg = leg[0]
        const arrivalLeg = leg[1]
        const departureAirport = departureLeg.airport
        const arrivalAirport = arrivalLeg.airport
        return `${departureAirport?.name} (${departureAirport?.iata}) - ${arrivalAirport?.name} (${arrivalAirport?.iata})`
    }).filter(Boolean).join(", ")

    let summaryString = ""
    if (responseStatus === CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.success) {
        if (returnId) {
            summaryString = t("ACTIVITIES.FLIGHT.CALCULATION_SUCCESSFUL_FOR_LEGS_WITH_RETURN_FLIGHT")
        } else {
            summaryString = t("ACTIVITIES.FLIGHT.CALCULATION_SUCCESSFUL_FOR_LEGS_WITHOUT_RETURN_FLIGHT")
        }
    } else {
        summaryString = t("ACTIVITIES.FLIGHT.CALCULATION_FAILED_FOR_LEGS")
    }

    return `${summaryString} ${failedLegsString}. ${emissionsCo2EString.length > 0 && t("ACTIVITIES.FLIGHT.CALCULATION_DONE_FOR_LEGS") + " " + emissionsCo2EString}`
}

/**
 * Generates a formatted string containing the IATA code, airport name, and country.
 *
 * @param {AirportResponse} airport - An object containing details about the airport.
 * @param {string} airport.iata - The IATA code of the airport.
 * @param {string} airport.name - The name of the airport.
 * @param {string} airport.country - The country where the airport is located.
 * @returns {string} - A string formatted as
 *                   -> IATA - Airport Name (Country)
 */
export const generateAirportIataAirportNameAndCountryString = (airport: AirportResponse): string => `${airport.iata} - ${airport.name} (${airport.country})`

/**
 * Generates a string that combines the departure and arrival airport information,
 * including IATA code, airport name, and country, in a specific formatted pattern.
 *
 * @param {AirportResponse} departure - An object containing data for the departure airport.
 * @param {AirportResponse} arrival - An object containing data for the arrival airport.
 * @returns {string} A string in the format 'Departure_Airport_Info [ROUTE_SEPARATOR] Arrival_Airport_Info',
 *                   where each airport info includes its IATA code, name, and country.
 */
export const generateDepartureArrivalAirportWithCountryString = (departure: AirportResponse, arrival: AirportResponse): string => `${generateAirportIataAirportNameAndCountryString(departure)} ${ROUTE_SEPARATOR} ${generateAirportIataAirportNameAndCountryString(arrival)}`

/**
 * Generates a connection string by processing an array of flight response objects.
 *
 * This function takes an array of flight response objects and concatenates
 * the airport information (IATA code, airport name, and country) of both
 * departure and arrival airports for each flight leg. The output is a
 * collection of connection strings separated by a route separator.
 *
 * @param {FlightsResponse[]} flightResponse - An array of flight response objects where each object represents a flight leg.
 * @returns {string[]} An array of formatted connection strings for each flight leg in the provided flight response.
 */
export const generateConnectionStringFromCalculationResponseString = (flightResponse: FlightsResponse[]): string[] => flightResponse.flatMap(leg => [generateAirportIataAirportNameAndCountryString(leg.departureAirport), generateAirportIataAirportNameAndCountryString(leg.arrivalAirport)].join(ROUTE_SEPARATOR))

/**
 * Constructs a localized string that describes the flight trip details and the number of passengers.
 * The function dynamically generates a description based on whether the trip is a return flight,
 * the count of flights, the number of passengers, and whether the string should indicate a return flight only.
 *
 * @param {boolean} isReturnFlight - Indicates if the trip includes a return flight.
 * @param {number} flightCount - The total number of flights in the trip.
 * @param {number} passengerCount - The total number of passengers for the flight trip.
 * @param {boolean} indicateThatIsOnlyReturnFlight - Determines if the string should specifically mention only the return flight.
 * @param {i18n} i18n - The internationalization object used for translating and localizing strings.
 * @returns {string} - A localized string describing the flight trip and the passenger count.
 *                   -> One-way for 1 Person
 */
export const createFlightTripPassengersCountString = (
    isReturnFlight: boolean,
    flightCount: number,
    passengerCount: number,
    indicateThatIsOnlyReturnFlight: boolean,
    i18n: i18n,
): string => {
    const {t} = i18n
    let commonString = ''
    if (indicateThatIsOnlyReturnFlight) {
        commonString = `${t('ACTIVITIES.FLIGHT.RETURN_FLIGHT_SINGULAR')} ${t("GENERAL.FOR")} ${passengerCount} ${generatePassengerString(passengerCount, i18n)}`
    } else {
        commonString = `${t(generateFlightTypeString(1, isReturnFlight))} ${t("GENERAL.FOR")} ${passengerCount} ${generatePassengerString(passengerCount, i18n)}`
    }
    if (flightCount > 0) return `${flightCount} ${commonString}`
    return commonString
}

/**
 * Generates a string describing improvements in flight emission calculations.
 *
 * This function utilizes internationalization (i18n) to fetch localized strings
 * and combines them with a predefined constant, `FLIGHT_CALCULATION_HINT`.
 * This is meant to convey specific hints about possibilities for better
 * emission calculations related to flights.
 *
 * @param {Object} i18n - The internationalization object containing localization methods.
 * @param {Function} i18n.t - Translation function to fetch localized strings.
 * @returns {string} A localized and formatted string suggesting improvements in flight emission calculations.
 */
export const generatFlightCalculationImprovementsString = (i18n: i18n): string => {
    const {t} = i18n
    return `${FLIGHT_CALCULATION_HINT}${t("HINTS.EMISSION_CALCULATION.FLIGHT.BETTER_CALCULATION_POSSIBILITY_SPECIFIC")}`
}

/**
 * Generates a route string by combining the departure and arrival locations
 * with a predefined route separator.
 *
 * @param {string} departure - The departure location identifier.
 * @param {string} arrival - The arrival location identifier.
 * @returns {string} - A concatenated string representing the route, separated by the route separator.
 *                   -> BER --- DOH
 */
export const generateDepartureArrivalRouteString = (departure: string, arrival: string): string => `${departure}${ROUTE_SEPARATOR}${arrival}`

/**
 * Generates a formatted string representing the departure and arrival airports using their names and IATA codes.
 *
 * @param {AirportResponse} departureAirport - Object representing the departure airport, including its name and IATA code.
 * @param {AirportResponse} arrivalAirport - Object representing the arrival airport, including its name and IATA code.
 * @returns {string} A formatted string in the format "DepartureAirportName (IATA) ROUTE_SEPARATOR ArrivalAirportName (IATA)".
 */
export const generateDepartureArrivalAirportWithIataString = (departureAirport: AirportResponse, arrivalAirport: AirportResponse) => `${departureAirport.name} (${departureAirport.iata}) ${ROUTE_SEPARATOR} ${arrivalAirport.name} (${arrivalAirport.iata})`